import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  useMediaQuery,
  TextField,
  Box,
  Button,
} from '@mui/material';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const agreementText = `
ANTHRASYNC SOLUTIONS PRIVATE LIMITED, a Private Limited Company incorporated under the Companies Act, 2013, having its registered office at No 26, Laskar Hosur Road, Adugodi, Bangalore South, Bengaluru, Karnataka, India - 560030 (hereinafter referred to as the "Company", "Platform", "we", "us", or "our"),
AND
 the individual who wants to become an Expert on the Platform (hereinafter referred to as the "Expert" or "you").

1. ELIGIBILITY & ONBOARDING
1.1 You represent and warrant that you are at least 18 years of age, legally competent to enter into this Agreement, and possess the necessary expertise and qualifications to provide services on the Platform.
1.2 The Company may verify your credentials, background, and identity at any time as part of onboarding or periodically thereafter.

2. SCOPE OF SERVICES
2.1 You agree to provide knowledge exchange sessions via the Platform in your area of expertise.
2.2 All sessions will be conducted online in real-time live mode using audio, video, or chat as facilitated by the Platform. No recorded or pre-recorded sessions are permitted unless explicitly approved by the Company.
2.3 You will conduct sessions professionally, ethically, and in compliance with applicable laws.
2.4 You shall not offer services requiring regulatory licenses unless duly licensed.

3. SESSION MANAGEMENT & CONTENT APPROVAL
3.1 You may choose the date, time, language, and topic for each session through the Platform interface.
3.2 You must submit session content (presentation, outline, or materials) at least 7 days prior to the session for Company approval.
3.3 Sessions can only be conducted after receiving approval of the submitted content.

4. SESSION FEEDBACK & QUALITY MONITORING
4.1 You agree to accept feedback from users and the Company to improve session quality.
4.2 The Company may monitor sessions from time to time for training, compliance, or quality assurance purposes.

5. PRICING, REVENUE SHARE & TAXATION
5.1 You may set your own booking price per end-user session.
5.2 Applicable GST and payment gateway charges will be added on top of your booking price and borne by the end-user.
5.3 Revenue split per booking is as follows:
  - Expert Earnings: 50% to 80%
  - Platform/Service Fee: 15% to 30%
  - Sales Representative Fee (if applicable): Additional 5% to 20%

5.4 Applicable TDS or other statutory deductions will be made before payment.
5.5 Payments will be made to your Indian bank account on a monthly basis via NEFT/IMPS.
5.6 You are responsible for your own tax declarations and compliances under Indian law, including GST registration and invoicing if applicable.

6. INTELLECTUAL PROPERTY
6.1 You retain ownership of all content and materials you create.
6.2 By submitting content and conducting sessions on the Platform, you grant the Company a non-exclusive, royalty-free license to use such materials for quality control, marketing, and user access purposes.
6.3 You acknowledge and consent that sessions conducted on the Platform may be recorded by the Company. By participating in or conducting sessions, you grant the Company the right to use, store, edit, and publish the recorded session videos, in whole or in part, for purposes such as platform content, marketing, training, quality assurance, and user engagement.

7. CONFIDENTIALITY & DATA PROTECTION
7.1 You agree to maintain confidentiality of any sensitive user data or Company proprietary information.
7.2 You shall comply with applicable Indian data privacy laws and any relevant international regulations.

8. CODE OF CONDUCT
8.1 You shall behave professionally and respectfully at all times.
8.2 Any abusive, unlawful, or unethical behavior will be subject to disciplinary action including suspension or termination.
8.3 You shall not solicit or conduct transactions outside the Platform.

9. INDEPENDENT CONTRACTOR
9.1 You act as an independent contractor and not as an employee, partner, or agent of the Company.
9.2 You are solely responsible for your tax and statutory obligations.

9A. EXTERNAL EMPLOYMENT AND ASSOCIATIONS
9A.1 The Company has no objection to your employment with any other company, or your status as an employer, or your association with any other institute or organization, provided that such employment or association does not result in a conflict of interest or violate the confidentiality and conduct clauses of this Agreement.

10. TERMINATION
10.1 You can opt out any time.
10.2 The Company reserves the right to suspend or terminate your access for breaches, misconduct, or inactivity.

11. LIMITATION OF LIABILITY
To the extent permitted by law, the Company shall not be liable for indirect, incidental, or consequential damages arising from this Agreement.

12. GOVERNING LAW & JURISDICTION
This Agreement is governed by the laws of India. The courts of Bengaluru, Karnataka shall have exclusive jurisdiction.

13. MISCELLANEOUS
This Agreement represents the entire understanding. Amendments must be in writing and signed by both parties.
In case of any disputes arising under or in connection with this Agreement, the final and binding decision shall rest solely with the Company.
`;


const ExpertAgreement = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const paperRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const element = paperRef.current;
    const regex = new RegExp(searchTerm, 'i');
    const match = agreementText.match(regex);

    if (match && element) {
      const index = agreementText.search(regex);
      setHighlightedIndex(index);

      const beforeMatch = agreementText.slice(0, index);
      const lines = beforeMatch.split('\n');
      const targetLine = lines.length - 1;
      const lineHeight = 24;
      const scrollTop = targetLine * lineHeight;

      element.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  };

  const getHighlightedText = (text, highlight) => {
    if (!highlight.trim()) return text;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <mark key={i} style={{ backgroundColor: '#ffe600', color: '#000' }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div style={{ backgroundColor: '#000000', minHeight: '100vh' }}>
      <Header1 />

      <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          gutterBottom
          sx={{
            fontWeight: 600,
            textAlign: 'center',
            fontFamily: 'Verdana, sans-serif',
            color: '#ffffff',
          }}
        >
          Expert Agreement
        </Typography>

        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'stretch' : 'center'}
          gap={2}
          mb={2}
        >
          <TextField
            label="Search"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputProps={{ style: { fontFamily: 'Verdana, sans-serif', color: '#ffffff' } }}
            InputLabelProps={{ style: { color: '#ffffff' } }}
            sx={{
              backgroundColor: '#1a1a1a',
              input: { color: '#ffffff' },
              label: { color: '#ffffff' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#555' },
                '&:hover fieldset': { borderColor: '#888' },
                '&.Mui-focused fieldset': { borderColor: '#fff' },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{
              fontFamily: 'Verdana, sans-serif',
              bgcolor: '#333',
              color: '#fff',
              '&:hover': { bgcolor: '#555' },
            }}
          >
            Search
          </Button>
        </Box>

        <Paper
          ref={paperRef}
          elevation={4}
          sx={{
            p: { xs: 2, sm: 3 },
            maxHeight: '75vh',
            overflowY: 'auto',
            whiteSpace: 'pre-line',
            backgroundColor: '#080808',
            color: '#e0e0e0',
            borderRadius: 2,
            fontFamily: 'Verdana, sans-serif',
            lineHeight: 1.6,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.5)',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#555',
              borderRadius: '3px',
            },
          }}
        >
          <Typography variant="body2" component="div" sx={{ fontFamily: 'Verdana, sans-serif' }}>
            {getHighlightedText(agreementText, searchTerm)}
          </Typography>
        </Paper>
      </Container>

      <Footer2 />
    </div>
  );
};

export default ExpertAgreement;
